<!--
  @component

  The textfield component

  ```html
  <oc-textfield-v1 label="Your first name" name="firstname" placeholder="Max"></oc-textfield-v1>
  ```
-->
<svelte:options
  customElement={{
    tag: "oc-textfield-v1",

    props: {
      type: { type: "String" },
      placeholder: { type: "String" },
      name: { type: "String", reflect: true }, /*                                    */
      value: { type: "String" }, /*                                     */
      label: { type: "String" },
      hint: { type: "String" },
      pattern: { type: "String" },
      variant: { type: "String" },
      autocomplete: { type: "String" },
      required: { type: "Boolean" },
      minlength: { type: "Number" },
      maxlength: { type: "Number" },
      prefixText: { type: "String", attribute: "prefix-text" },
      suffixText: { type: "String", attribute: "suffix-text" },
      prefixIcon: { type: "String", attribute: "prefix-icon" },
      suffixIcon: { type: "String", attribute: "suffix-icon" },
      disabled: { type: "Boolean", reflect: true }, /*                                    */
    },
    /*                                                         */
    /*         */
    extend: (CustomElementConstructor) =>
      class extends CustomElementConstructor {
        /*                                          */
        static formAssociated = true;

        constructor() {
          super();

          /*                                */
          this.internals = this.attachInternals();
        }

        /*                       */
        formResetCallback() {
          /*                                   */
          this.value = this.getAttribute("value") || "";
        }
      },
  }}
/>

<script lang="ts">
  import type { IconType } from "@otto-ec/icons";
  import HintAndCounterV1 from "../../../../common/components/HintAndCounterV1.svelte";
  import { usePropertyChange } from "../../../../common/utils/usePropertyChange";

  usePropertyChange();

  /**
 *
 */
  export let name: string;

  /**
 *
 *
 *
 *
 *
 *
 */
  export let value: string = "";

  /**
 *
 *
 *
 */
  export let label: string;

  /**
 *
 */
  export let hint: string | undefined = undefined;

  /**
 *
 *
 *
 */
  export let variant: "default" | "error" | "success" | "warning" = "default";

  /**
 *
 *
 *
 */
  export let required: boolean = false;

  /**
 *
 *
 *
 */
  export let disabled: boolean = false;

  /**
 *
 *
 *
 */
  export let pattern: string | undefined = undefined;

  /**
 *
 *
 *
 *
 */
  export let autocomplete:
    | "on"
    | "off"
    | "address-line1"
    | "address-line2"
    | "address-line3"
    | "address-level1"
    | "address-level2"
    | "address-level3"
    | "address-level4"
    | "street-address"
    | "country"
    | "country-name"
    | "postal-code"
    | "name"
    | "additional-name"
    | "family-name"
    | "give-name"
    | "honoric-prefix"
    | "honoric-suffix"
    | "nickname"
    | "organization-title"
    | "username"
    | "new-password"
    | "current-password"
    | "bday"
    | "bday-day"
    | "bday-month"
    | "bday-year"
    | "sex"
    | "one-time-code"
    | "organization"
    | "cc-name"
    | "cc-given-name"
    | "cc-additional-name"
    | "cc-family-name"
    | "cc-number"
    | "cc-exp"
    | "cc-exp-month"
    | "cc-exp-year"
    | "cc-csc"
    | "cc-type"
    | "transaction-currency"
    | "transaction-amount"
    | "language"
    | "url"
    | "email"
    | "photo"
    | "tel"
    | "tel-country-code"
    | "tel-national"
    | "tel-area-code"
    | "tel-local"
    | "tel-local-prefix"
    | "tel-local-suffix"
    | "tel-extension"
    | "impp" = "off";

  /**
 *
 *
 *
 */
  export let placeholder: string | undefined = undefined;
  /*                                                                          */
  $: nonEmptyPlaceholder = placeholder || " ";

  /*                                                    */
  /**
 *
 *
 *
 *
 *
 *
 *
 *
 *
 */
  /*                                                                                                         */
  export let type:
    | "email"
    | "number"
    | "password"
    | "tel"
    | "text"
    | "url"
    | "integer"
    | "decimal" = "text";
  /*                             */
  $: nativeInputType = ["integer", "decimal"].includes(type) ? "number" : type;

  /**
 *
 *
 *
 */
  export let minlength: number | undefined = undefined;

  /**
 *
 *
 *
 */
  export let maxlength: number | undefined = undefined;

  /**
 *
 */
  export let prefixText: string | undefined = undefined;

  /**
 *
 */
  export let suffixText: string | undefined = undefined;

  /**
 *
 */
  export let prefixIcon: IconType | undefined = undefined;

  /**
 *
 */
  export let suffixIcon: IconType | undefined = undefined;

  /*            */
  export let internals: ElementInternals;

  let inputElement: HTMLInputElement;

  $: if (inputElement) {
    internals.setFormValue(value);
    if (!internals?.form?.noValidate) {
      internals.setValidity(inputElement.validity, inputElement.validationMessage, inputElement);
    }
  }

  /*                                    */
  const onInput = ({ target }: InputEvent) => {
    value = target.value;
  };

  /*               */
  const focus = () => {
    inputElement?.focus();
  };

  /*                                                      */
  const selectText = () => {
    inputElement?.select();
  };

  /*                                          */
  const beforeInput = (event: InputEvent) => {
    if (event.data) {
      if (type === "decimal" && !/^[0-9.,]*$/.test(event.data)) {
        event.preventDefault();
      } else if (type === "integer" && !/^\d*$/.test(event.data)) {
        event.preventDefault();
      }
    }
  };

  /*                                       */
  $: ariaDescribedById = hint && "hint";
</script>

<div
  class="textfield"
  class:textfield--error={variant === "error"}
  class:textfield--warning={variant === "warning"}
  class:textfield--success={variant === "success"}
  class:textfield--disabled={disabled}
  role="none"
  on:click={focus}
  on:dblclick={selectText}
>
  {#if prefixText}
    <small class="textfield__prefix-text">{prefixText}</small>
  {/if}
  {#if prefixIcon}
    <oc-icon-v1 class="textfield__prefix-icon" type={prefixIcon} />
  {/if}

  <input
    class="textfield__input"
    id="input"
    bind:this={inputElement}
    {value}
    type={nativeInputType}
    placeholder={nonEmptyPlaceholder}
    {required}
    {disabled}
    {name}
    {pattern}
    {minlength}
    {maxlength}
    {autocomplete}
    aria-describedby={ariaDescribedById}
    on:beforeinput={beforeInput}
    on:input={onInput}
  />

  {#if suffixText}
    <small class="textfield__suffix-text">{suffixText}</small>
  {/if}
  {#if suffixIcon}
    <oc-icon-v1 class="textfield__suffix-icon" type={suffixIcon} />
  {/if}

  <label class="textfield__label" for="input">{label}</label>
</div>

<HintAndCounterV1
  hintId="hint"
  {variant}
  {hint}
  {disabled}
  minCounter={minlength}
  maxCounter={maxlength}
  counterValue={value}
/>

<style lang="scss">
  @use "./TextfieldV1.config" as config;
  @use "@otto-ec/design-tokens/tokens";

  $autofill-background-color: #f0f4fc;

  :host {
    @include config.oc-textfield;
  }

  :host[minlengt],
  :host[maxlength],
  :host[hint] {
    @include config.oc-textfield-hint;
  }

  .textfield {
    box-sizing: border-box;
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;

    padding: tokens.$oc-component-form-field-padding-y tokens.$oc-component-form-field-padding-x;

    background: tokens.$oc-component-form-background-color;
    outline: 1px solid tokens.$oc-component-form-default-border-color;
    border-radius: tokens.$oc-component-form-field-border-radius;
    cursor: text;

    &:hover:not(.textfield--disabled) {
      outline-width: 2px;
    }

    &:focus-within:not(.textfield--error):not(.textfield--warning):not(.textfield--success) {
      outline-color: tokens.$oc-component-form-default-border-color-focus;
      outline-width: 2px;
    }

    &__input {
      all: unset;
      width: 100%;

      font: tokens.$oc-component-form-field-input-font;
      color: tokens.$oc-component-form-field-input-color;

      &::placeholder {
        font: tokens.$oc-component-form-field-placeholder-font;
        color: tokens.$oc-component-form-field-placeholder-color;
        opacity: 0;
        transition: opacity 0.1s cubic-bezier(0.4, 0, 0.2, 1);
        white-space: nowrap;
        text-overflow: ellipsis;
        max-width: 100%;
        box-sizing: border-box;
      }
    }

    &__label {
      position: absolute;
      top: tokens.$oc-component-form-field-padding-y;
      left: tokens.$oc-semantic-spacing-50;
      padding: 0 tokens.$oc-semantic-spacing-25;
      max-width: calc(100% - 2 * tokens.$oc-semantic-spacing-50);
      box-sizing: border-box;

      transition: all 0.1s cubic-bezier(0.4, 0, 0.2, 1);

      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      user-select: none;
      cursor: text;
      font: tokens.$oc-component-form-label-font;
      background-color: tokens.$oc-component-form-background-color;
      border-radius: tokens.$oc-component-form-field-label-border-radius;
      color: tokens.$oc-component-form-default-label-color;
    }

    &--error {
      outline-color: tokens.$oc-component-form-error-border-color;

      .textfield__label {
        color: tokens.$oc-component-form-error-label-color;
      }
    }

    &--warning {
      outline-color: tokens.$oc-component-form-warning-border-color;
    }

    &--success {
      outline-color: tokens.$oc-component-form-success-border-color;

      .textfield__label {
        color: tokens.$oc-component-form-success-label-color;
      }
    }

    &--disabled {
      cursor: default;
      outline-color: tokens.$oc-component-form-disabled-border-color;
      background-color: tokens.$oc-component-form-disabled-background-color;

      .textfield__input {
        color: tokens.$oc-component-form-disabled-input-color;
      }

      .textfield__label {
        cursor: default;
        background-color: tokens.$oc-component-form-disabled-background-color;
        color: tokens.$oc-component-form-disabled-label-color;
      }
    }

    .textfield__prefix-text,
    .textfield__suffix-text {
      font: tokens.$oc-component-form-field-input-font;
      user-select: none;
      flex-shrink: 0;
    }

    .textfield__prefix-icon,
    .textfield__suffix-icon {
      flex-shrink: 0;
    }

    &__input:-webkit-autofill, /*                                                        */
    &:active:not(.textfield--disabled) .textfield__input, /*                                      */
    &:focus-within .textfield__input,
    &__input:not(:placeholder-shown) {
      &::placeholder {
        opacity: 1;
      }

      ~ .textfield__label {
        top: -8px;
        left: tokens.$oc-semantic-spacing-50;
        padding: 0 tokens.$oc-semantic-spacing-25;
        font: tokens.$oc-semantic-font-copy-75;
      }
    }

    /*                           */
    &__input {
      &:-webkit-autofill {
        -webkit-box-shadow: 0 0 0 50px $autofill-background-color inset;
        -webkit-text-fill-color: tokens.$oc-component-form-field-input-color;
      }
    }

    /*                                                                                 */
    &:has(.textfield__input:-webkit-autofill) {
      background: $autofill-background-color;
    }
  }
</style>
